/**
 * The server returns replies mixed into the same flat oldest-first list as the
 * comments they answer. A reply whose parent is filtered out (resolved and
 * hidden) or missing entirely never renders on its own: it belongs to a thread,
 * so it follows the thread's fate.
 */
import { isYesterday } from 'date-fns';

import { i18n } from '@/shared/i18n';
import { formatTimeAgo } from '@/shared/utils/dateUtils';

import type { TrackerCommentResponseDto } from '@/entities/tracker-comment';

export interface TrackerCommentThread {
  comment: TrackerCommentResponseDto;
  replies: TrackerCommentResponseDto[];
}

export interface BuildCommentThreadsOptions {
  showResolved: boolean;
  newestFirst: boolean;
}

export function buildCommentThreads(
  items: TrackerCommentResponseDto[],
  { showResolved, newestFirst }: BuildCommentThreadsOptions,
): TrackerCommentThread[] {
  const repliesByParent = new Map<string, TrackerCommentResponseDto[]>();
  for (const item of items) {
    // Truthiness rather than `=== null`: an API old enough to omit the field
    // entirely (a pod mid-deploy, a response cached from before threads
    // shipped) would otherwise make every comment an orphan and empty the panel.
    if (!item.parentId) continue;
    const siblings = repliesByParent.get(item.parentId);
    if (siblings) siblings.push(item);
    else repliesByParent.set(item.parentId, [item]);
  }

  const roots = items.filter((c) => !c.parentId && (showResolved || !c.resolved));
  const ordered = newestFirst ? [...roots].reverse() : roots;

  // Replies stay oldest-first whichever way the roots are ordered — a thread
  // reads as a conversation, not as a second inverted list.
  return ordered.map((comment) => ({
    comment,
    replies: repliesByParent.get(comment.id) ?? [],
  }));
}

/**
 * Timestamp label for a comment card. `formatTimeAgo` covers everything but
 * the two cases a reader judges against the clock on the wall: "yesterday"
 * means the previous calendar day, not 24 to 48 hours ago, and a comment
 * posted seconds ago reads as bare "now" beside a name.
 */
export function formatCommentTimestamp(iso: string): string {
  const created = new Date(iso);
  if (Date.now() - created.getTime() < 60 * 1000) {
    return i18n.t('trackers:trackersBoard.trackerCommentsPanel.justNow');
  }
  if (isYesterday(created)) {
    return i18n.t('trackers:trackersBoard.trackerCommentsPanel.yesterday');
  }
  return formatTimeAgo(iso);
}
